import { useState, useMemo } from 'react';
import { useNavigate, useMatch, useParams } from 'react-router-dom';
import type { Project } from '@/types/entities';
import { PageHeader } from '@/components/composites/PageHeader';
import { DataTable } from '@/components/composites/DataTable';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { ActionLink } from '@/components/primitives/ActionLink';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Select } from '@/components/primitives/Select';
import { TextInput } from '@/components/primitives/TextInput';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { formatDayFull } from '@/lib/dates';
import { noop } from '@/lib/noop';
import styles from './ManageProjectsPage.module.css';

interface ProjectRow {
  project: Project;
  clientName: string | null;
}

export function ManageProjectsPage() {
  const navigate = useNavigate();
  const editMatch = useMatch('/projects/:projectId/edit');
  const { projectId: editProjectId } = useParams<{ projectId: string }>();

  const { data: projects } = useProjects();
  const { data: clients } = useClients();

  const [clientFilter, setClientFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editBillable, setEditBillable] = useState(true);

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.name })),
    [clients],
  );

  const typeOptions = [
    { value: 'billable', label: 'Billable' },
    { value: 'general', label: 'General' },
  ] as const;

  const rows: ProjectRow[] = useMemo(() => {
    return projects
      .map((p) => {
        const client = p.clientId ? (clients.find((c) => c.id === p.clientId) ?? null) : null;
        return { project: p, clientName: client?.name ?? null };
      })
      .filter((row) => {
        if (clientFilter && row.project.clientId !== clientFilter) return false;
        if (typeFilter && row.project.type !== typeFilter) return false;
        if (search) {
          const q = search.toLowerCase();
          if (
            !row.project.name.toLowerCase().includes(q) &&
            !(row.clientName ?? '').toLowerCase().includes(q)
          )
            return false;
        }
        return true;
      });
  }, [projects, clients, clientFilter, typeFilter, search]);

  const editingProject = editProjectId ? projects.find((p) => p.id === editProjectId) : undefined;
  // Guard the edit panel for system-owned projects even if the user navigates
  // directly to the URL — the backend returns 409 SYSTEM_OWNED on PATCH, so
  // rendering the form would be misleading. Disabled affordances in the table
  // already prevent normal navigation here.
  const editPanelBlocked = !!editingProject && editingProject.systemOwned;
  const panelOpen = !!editMatch && !editPanelBlocked;
  const closePanel = () => navigate('/projects');

  type Col = 'project' | 'client' | 'type' | 'slug' | 'created' | 'actions';

  const columns: Array<{
    key: Col;
    header: string;
    align?: 'left' | 'right';
    render: (row: ProjectRow) => React.ReactNode;
  }> = [
    {
      key: 'project',
      header: 'Project',
      render: (row) => <span className={styles.projectName}>{row.project.name}</span>,
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => (row.clientName ? row.clientName : <>&mdash;</>),
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => (
        <Badge variant={row.project.type}>
          {row.project.type[0].toUpperCase() + row.project.type.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      render: (row) => (
        <span className={styles.slugCell}>
          {row.project.name.toLowerCase().replace(/\s+/g, '-')}
        </span>
      ),
    },
    {
      key: 'created',
      header: 'Created',
      render: (row) => (
        <span className={styles.createdCell}>{formatDayFull(row.project.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => {
        const locked = row.project.systemOwned;
        const lockedTitle = 'System-owned — managed by Kanjoy';
        return (
          <div className={styles.actionsCell}>
            <ActionLink
              disabled={locked}
              title={locked ? lockedTitle : undefined}
              onClick={locked ? undefined : () => navigate(`/projects/${row.project.id}/edit`)}
            >
              Edit
            </ActionLink>
            <span className={styles.actionSep}>&middot;</span>
            <ActionLink
              danger
              disabled={locked}
              title={locked ? lockedTitle : undefined}
              onClick={locked ? undefined : noop}
            >
              Delete
            </ActionLink>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Projects" />

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <Select
          value={clientFilter}
          options={clientOptions}
          placeholder="All Clients"
          onChange={(v) => setClientFilter(v)}
          size="sm"
        />
        <Select
          value={typeFilter}
          options={typeOptions}
          placeholder="All"
          onChange={(v) => setTypeFilter(v)}
          size="sm"
        />
        <div className={styles.searchWrap}>
          <svg
            className={styles.searchIcon}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className={styles.filterSpacer} />
        <span className={styles.filterCount}>{rows.length} projects</span>
      </div>

      <DataTable<ProjectRow, Col>
        caption="All projects"
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.project.id}
      />

      {panelOpen && editingProject && (
        <SlidePanel
          open={panelOpen}
          onClose={closePanel}
          title="Edit Project"
          width="sm"
          footer={
            <div className={styles.panelFooter}>
              <Button variant="secondary" onClick={closePanel}>
                Cancel
              </Button>
              <Button variant="primary" onClick={noop}>
                Save Changes
              </Button>
            </div>
          }
        >
          <div className={styles.panelBody}>
            <TextInput label="Name" defaultValue={editingProject.name} required />
            <Select
              label="Client"
              value={editingProject.clientId ?? ''}
              options={clientOptions}
              placeholder="No client (org-level)"
              onChange={noop}
            />
            <TextInput
              label="Slug"
              defaultValue={editingProject.name.toLowerCase().replace(/\s+/g, '-')}
              hint="Changing the slug may break existing integrations."
              className={styles.monoInput}
            />
            <Checkbox
              checked={editBillable}
              onChange={setEditBillable}
              label="This project's hours are billable to the client"
            />
            <div className={styles.metaSection}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Created</span>
                <span className={styles.metaValue}>{formatDayFull(editingProject.createdAt)}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Total Hours</span>
                <span className={`${styles.metaValue} ${styles.monoValue}`}>156.2h</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Total Events</span>
                <span className={`${styles.metaValue} ${styles.monoValue}`}>1,874</span>
              </div>
            </div>
          </div>
        </SlidePanel>
      )}
    </>
  );
}
